import { useState } from "react";
import { chevronUpIcon, chevronDownIcon } from "@progress/kendo-svg-icons";
import { SvgIcon } from "@progress/kendo-react-common";
import { MaskedAccountField } from "@/app/utils/MaskedAccountField";
import { motion, AnimatePresence } from "framer-motion";
import {
  checkCircleIcon,
  xCircleIcon,
  clockIcon,
} from "@progress/kendo-svg-icons";
import { ICON_BASE_PATH } from "@/app/utils/config";
import { gxDateTimeFormat } from "@/app/utils/helpers/gxDateTimeFormat";
import { t } from "i18next";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import "../../accounts/TransactionLayout.scss";
import "../../rtp/manageRtpRequests/ManageRTPRequests.scss";
import { Constants } from "@/app/utils/AppConstants";

// ============================================================================
// STATUS HELPERS — mirrors ApprovalProgress pattern
// ============================================================================

const mapApproverStatusToLetter = (
  raw: string | undefined | null,
): "A" | "R" | "P" | "SFR" | "INBANK" => {
  if (!raw) return "P";

  const s = raw
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");

  if (s === "approved" || s === "selfapproved" || s === "3000" || s === "3001")
    return "A";
  if (s === "rejected" || s === "3020") return "R";
  if (s === "sentforrepair" || s === "sfr") return "SFR";
  if (s === "inbank") return "INBANK";
  return "P";
};

const getStepSvgIcon = (status: "A" | "R" | "P") => {
  if (status === "A") return checkCircleIcon;
  if (status === "R") return xCircleIcon;
  return clockIcon;
};

const getStepIconColor = (status: "A" | "R" | "P"): string => {
  if (status === "A") return "#16a34a";
  if (status === "R") return "#ef4444";
  return "#fb923c";
};

const getStepLineClass = (status: "A" | "R" | "P"): string => {
  if (status === "A")
    return "payment-progress__line payment-progress__line--ok";
  if (status === "R")
    return "payment-progress__line payment-progress__line--error";
  return "payment-progress__line payment-progress__line--warn";
};

const getApproverImgIcon = (
  status: "A" | "R" | "P" | "SFR" | "INBANK",
): string => {
  switch (status) {
    case "A":
      return `${ICON_BASE_PATH}green_tick.svg`;
    case "R":
      return `${ICON_BASE_PATH}failed-icon.svg`;
    case "SFR":
      return `${ICON_BASE_PATH}s-f-r-icon.svg`;
    case "INBANK":
      return `${ICON_BASE_PATH}pending-icon.svg`;
    default:
      return `${ICON_BASE_PATH}waiting.svg`;
  }
};

const getApproverStatusLabel = (
  status: "A" | "R" | "P" | "SFR" | "INBANK",
): string => {
  switch (status) {
    case "A":
      return "Approved";
    case "R":
      return "Rejected";
    case "SFR":
      return "Sent For Repair";
    case "INBANK":
      return "In Bank";
    default:
      return "Pending";
  }
};

// ============================================================================
// PAYMENT STATUS CODE → human label
// ============================================================================

const getPaymentStatusLabel = (code: number): string => {
  switch (code) {
    case Constants.awaitingApproval:
      return "Awaiting Approval";
    case Constants.sentForRepairCode:
      return "Sent For Repair";
    case Constants.rejectedByApprover:
      return "Rejected By Approver";
    case Constants.approvedByApprover:
      return "Approved By Approver";
    case Constants.selfApproved:
      return "Self Approved";
    case Constants.pendingConsentAuthorization:
      return "Pending Consent Authorization";
    case Constants.pendingWithBankSameCurrency:
    case Constants.pendingWithBankCrossCurrency:
      return "Pending With Bank";
    case Constants.inProcessWithBank:
      return "In Process With Bank";
    case Constants.rejectedByBank:
      return "Rejected By Bank";
    case Constants.processedByBank:
      return "Processed By Bank";
    case Constants.futureDatedPayment:
      return "Future Dated Payment";
    default:
      return "";
  }
};

const getOverallStepStatus = (code: number): "A" | "R" | "P" => {
  switch (code) {
    case Constants.approvedByApprover:
    case Constants.selfApproved:
    case Constants.processedByBank:
    case Constants.inProcessWithBank:
      return "A";
    case Constants.rejectedByApprover:
    case Constants.rejectedByBank:
      return "R";
    default:
      return "P";
  }
};

// ============================================================================
// APPROVER ROW
// ============================================================================

interface ApproverEntry {
  approverName: string;
  approvalDate: string;
  status: "A" | "R" | "P" | "SFR" | "INBANK";
  level: number;
}

const buildApproverList = (transaction: any): ApproverEntry[] => {
  const metromap = transaction?.paymentMetromap;

  // === Primary: paymentMetromap =====================================================================
  if (metromap?.approvalDetails?.approvers?.length) {
    const fromMetromap: ApproverEntry[] =
      metromap.approvalDetails.approvers.map((ap: any) => ({
        approverName: ap.approvedName || "",
        approvalDate: ap.approvedAt ? gxDateTimeFormat(ap.approvedAt) : "",
        status: mapApproverStatusToLetter(ap.approvedStatus),
        level: ap.aprdLevel ?? 0,
      }));

    const required: number =
      metromap.approvalDetails.approvalsRequired ?? fromMetromap.length;
    const padding = Math.max(0, required - fromMetromap.length);

    return [
      ...fromMetromap,
      ...Array.from({ length: padding }, (_, i) => ({
        approverName: "",
        approvalDate: "",
        status: "P" as const,
        level: fromMetromap.length + i + 1,
      })),
    ];
  }

  // === Fallback: authHistDTO ========================================================================
  if (
    Array.isArray(transaction?.authHistDTO) &&
    transaction.authHistDTO.length
  ) {
    return transaction.authHistDTO.map((h: any, idx: number) => {
      const s = (h.aprdStatus ?? "").toUpperCase();
      const status: ApproverEntry["status"] =
        s === "A"
          ? "A"
          : s === "R"
            ? "R"
            : s === "SR"
              ? "SFR"
              : s === "S"
                ? "INBANK"
                : "P";
      return {
        approverName: h.aprdByUserName || "",
        approvalDate: h.aprdDt ? gxDateTimeFormat(h.aprdDt) : "",
        status,
        level: h.aprdLevel ?? idx + 1,
      };
    });
  }

  return [];
};

// ============================================================================
// COMPONENT
// ============================================================================

interface TransectionDetailsProps {
  transaction: any;
  schedule?: boolean;
}

export default function TransectionDetails({
  transaction,
  schedule = false,
}: Readonly<TransectionDetailsProps>) {
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(
    new Set([0, 1]),
  );
  const [isTransferFromOpen, setIsTransferFromOpen] = useState(true);
  const [isTransferToOpen, setIsTransferToOpen] = useState(true);
  const [isTransactionDetailsOpen, setIsTransactionDetailsOpen] =
    useState(true);
  const [isPaymentProgressOpen, setIsPaymentProgressOpen] = useState(true);

  const toggleStep = (index: number) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });
  };

  if (!transaction) {
    return (
      <div className="gx-transfer-container">
        <div className="flex justify-center items-center py-8">
          <div className="text-gray-500">{t("loading_detail")}</div>
        </div>
      </div>
    );
  }

  // === Derived timeline data ========================================================================─
  const metromap = transaction.paymentMetromap;
  const hasMetromap = !!metromap?.initiatedDetails;

  const initiatedBy: string =
    metromap?.initiatedDetails?.initiatedBy ||
    transaction.displayName ||
    transaction.debitAccountName ||
    "System";

  const initiatedAt: string = hasMetromap
    ? gxDateTimeFormat(metromap.initiatedDetails.initiatedAt)
    : gxDateTimeFormat(
        transaction.paymentInitiatedDate || transaction.crtdDt || "",
      );

  const approverList = buildApproverList(transaction);

  const approvalsRequired: number =
    metromap?.approvalDetails?.approvalsRequired ??
    transaction.numberOfLevels ??
    approverList.length;

  const approvalsCompleted: number =
    metromap?.approvalDetails?.approvalsCompleted ??
    transaction.numberOfApproved ??
    approverList.filter((a) => a.status === "A").length;

  const paymentStatusCode: number = transaction.paymentStatusCode;
  const overallStepStatus = getOverallStepStatus(paymentStatusCode);
  const paymentStatusLabel =
    getPaymentStatusLabel(paymentStatusCode) ||
    transaction.paymentStatusDesc ||
    "Payment Status";

  const statusTimestamp: string = transaction.updtDt
    ? gxDateTimeFormat(transaction.updtDt)
    : transaction.paymentDate
      ? gxDateTimeFormat(transaction.paymentDate)
      : "";

  return (
    <div className="gx-transfer-container">
      {/* Reference Number Heading */}
      <div className="gx-transfer-heading">
        <div className="gx-transfer-heading_value gx-h2-24-b gx-bene-tr_details">
          {t("paid")}{" "}
          <span className="gx-transfer-heading_value gx-h2-24-b">
            <span>
              {
                gxCurrencyFormat(
                  transaction.debitAmount,
                  transaction.debitCurrency,
                  transaction.debitDenomination,
                  { showSymbol: true, showCode: false },
                ).symbol
              }
            </span>{" "}
            {
              gxCurrencyFormat(
                transaction.debitAmount,
                transaction.debitCurrency,
                transaction.debitDenomination,
                { showSymbol: true, showCode: false },
              ).integer
            }
            <span className="gx-oo-gray">.00</span>
          </span>
        </div>
        <span className="gx-cell-date-text gx-p10-m">
          {gxDateTimeFormat(
            transaction?.crtdDt || transaction?.paymentInitiatedDate || "--",
          )}
        </span>
      </div>

      {/* Transfer From */}
      <div className="gx-card mt-1">
        <div
          className="gx-card-header"
          onClick={() => setIsTransferFromOpen(!isTransferFromOpen)}
        >
          <h2 className="gx-rtp_view-head gx-p18-b">{t("transfer_from")}</h2>
          <SvgIcon
            icon={isTransferFromOpen ? chevronUpIcon : chevronDownIcon}
            size="large"
          />
        </div>
        {isTransferFromOpen && (
          <div className="gx-card-body">
            <div className="gx-row">
              <span className="gx-label gx-p12-b">{t("account_name")}</span>
              <span className="gx-rtp-value gx-p12-b">
                {transaction.debitAccountName || "--"}
              </span>
            </div>
            {transaction.accountNickname && (
              <div className="gx-row">
                <span className="gx-label gx-p12-b">
                  {t("account_nickname")}
                </span>
                <span className="gx-rtp-value gx-p12-b">
                  {transaction.accountNickname}
                </span>
              </div>
            )}
            <div className="gx-row">
              <span className="gx-label gx-p12-b">
                {t("account_number_iban")}
              </span>
              <span className="gx-p12-b">
                <MaskedAccountField
                  accountNumber={
                    transaction.debitAccountNo ||
                    transaction.debitIbanNo ||
                    "--"
                  }
                />
              </span>
            </div>
            <div className="gx-row">
              <span className="gx-label gx-p12-b">{t("bank_name")}</span>
              <span className="gx-rtp-value gx-p12-b">
                {transaction.debitBankName || "---"}
              </span>
            </div>
            <div className="gx-row">
              <span className="gx-label gx-p12-b">{t("country")}</span>
              <span className="gx-rtp-value gx-p12-b">
                {transaction.debitCountryName || "---"}
              </span>
            </div>
            <div className="gx-row">
              <span className="gx-label gx-p12-b">{t("transfer_type")}</span>
              <span className="gx-rtp-value gx-p12-b">
                {transaction.paymentTypeName || "---"}
              </span>
            </div>
            <div className="gx-row">
              <span className="gx-label gx-p12-b">{t("transfer_date")}</span>
              <span className="gx-rtp-value gx-p12-b">
                {transaction.paymentDate
                  ? gxDateTimeFormat(transaction.paymentDate)
                  : "--"}
              </span>
            </div>
            <div className="gx-row">
              <span className="gx-label gx-p12-b">
                {t("request_date_time")}
              </span>
              <span className="gx-rtp-value gx-p12-b">
                {transaction.paymentInitiatedDate
                  ? gxDateTimeFormat(transaction.paymentInitiatedDate)
                  : transaction.crtdDt
                    ? gxDateTimeFormat(transaction.crtdDt)
                    : "--"}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Transfer To */}
      <div className="gx-card mt-4">
        <div
          className="gx-card-header"
          onClick={() => setIsTransferToOpen(!isTransferToOpen)}
        >
          <h2 className="gx-rtp_view-head gx-p18-b">{t("transfer_to")}</h2>
          <SvgIcon
            icon={isTransferToOpen ? chevronUpIcon : chevronDownIcon}
            size="large"
          />
        </div>
        {isTransferToOpen && (
          <div className="gx-card-body">
            <div className="gx-row">
              <span className="gx-label gx-p12-b">{t("beneficiary_name")}</span>
              <span className="gx-rtp-value gx-p12-b">
                {transaction.beneAccountName || "--"}
              </span>
            </div>
            {transaction.beneNickname && (
              <div className="gx-row">
                <span className="gx-label gx-p12-b">
                  {t("beneficiary_nickname")}
                </span>
                <span className="gx-rtp-value gx-p12-b">
                  {transaction.beneNickname}
                </span>
              </div>
            )}
            <div className="gx-row">
              <span className="gx-label gx-p12-b">
                {t("account_number_iban")}
              </span>
              <span className="gx-p12-b">
                <MaskedAccountField
                  accountNumber={
                    transaction.beneAccountNo || transaction.beneIbanNo || "--"
                  }
                />
              </span>
            </div>
            <div className="gx-row">
              <span className="gx-label gx-p12-b">
                {t("beneficiary_address")}
              </span>
              <span className="gx-rtp-value gx-p12-b w-[50%] wrap-break-word">
                {transaction.beneAddressLine1 || "--"}
              </span>
            </div>
            <div className="gx-row">
              <span className="gx-label gx-p12-b">
                {t("beneficiary_bank_name")}
              </span>
              <span className="gx-rtp-value gx-p12-b">
                {transaction.beneBankName || "--"}
              </span>
            </div>
            <div className="gx-row">
              <span className="gx-label gx-p12-b">{t("swift_bic_code")}</span>
              <span className="gx-rtp-value gx-p12-b">
                {transaction.beneBankSwiftBicCode || "--"}
              </span>
            </div>
            <div className="gx-row">
              <span className="gx-label gx-p12-b">
                {t("beneficiary_bank_address")}
              </span>
              <span className="gx-rtp-value gx-p12-b w-[50%] wrap-break-word">
                {transaction.beneBankAddressLine1 || "--"}
              </span>
            </div>
            <div className="gx-row">
              <span className="gx-label gx-p12-b">
                {t("bank")} {t("country")}
              </span>
              <span className="gx-rtp-value gx-p12-b">
                {transaction.beneBankCountryName || "--"}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Transaction Details */}
      <div className="gx-card mt-4">
        <div
          className="gx-card-header"
          onClick={() => setIsTransactionDetailsOpen(!isTransactionDetailsOpen)}
        >
          <h2 className="gx-rtp_view-head gx-p18-b">
            {t("transaction_details")}
          </h2>
          <SvgIcon
            icon={isTransactionDetailsOpen ? chevronUpIcon : chevronDownIcon}
            size="large"
          />
        </div>
        {isTransactionDetailsOpen && (
          <div className="gx-card-body">
            <div className="gx-row">
              <span className="gx-label gx-p12-b">{t("payment_amount")}</span>
              <span className="gx-rtp-value gx-p12-b">
                {
                  gxCurrencyFormat(
                    transaction.debitAmount,
                    transaction.debitCurrency,
                    transaction.debitDenomination,
                    { showSymbol: true, showCode: false },
                  ).formatted
                }
              </span>
            </div>
            {transaction.chargeAmount > 0 && (
              <div className="gx-row">
                <span className="gx-label gx-p12-b">{t("charges")}</span>
                <span className="gx-rtp-value gx-p12-b">
                  {
                    gxCurrencyFormat(
                      transaction.chargeAmount,
                      transaction.chargeCurrency,
                      transaction.chargeDenomination,
                      { showSymbol: true, showCode: false },
                    ).formatted
                  }
                </span>
              </div>
            )}
            {transaction.chargeBearerType && (
              <div className="gx-row">
                <span className="gx-label gx-p12-b">{t("charge_bearer")}</span>
                <span className="gx-rtp-value gx-p12-b">
                  {transaction.chargeBearerType}
                </span>
              </div>
            )}
            {transaction.forexRate && transaction.forexRate !== 1.0 && (
              <div className="gx-row">
                <span className="gx-label gx-p12-b">{t("exchange_rate")}</span>
                <span className="gx-rtp-value gx-p12-b">
                  {transaction.forexRate}
                </span>
              </div>
            )}
            <div className="gx-row">
              <span className="gx-label gx-p12-b">
                {t("upi_transaction_id")}
              </span>
              <span className="gx-rtp-value gx-p12-b">
                {transaction.gxChnNo || "--"}
              </span>
            </div>
            <div className="gx-row">
              <span className="gx-label gx-p12-b">
                {t("purpose_of_payment")}
              </span>
              <span className="gx-rtp-value gx-p12-b">
                {transaction.purposeCodeName || "--"}
              </span>
            </div>
            <div className="gx-row">
              <span className="gx-label gx-p12-b">{t("payment_details")}</span>
              <span className="gx-rtp-value gx-p12-b">
                {transaction.paymentDetails || "--"}
              </span>
            </div>
            <div className="gx-row">
              <span className="gx-label gx-p12-b">
                {t("payment_reference")}
              </span>
              <span className="gx-rtp-value gx-p12-b">
                {transaction.paymentReference || "--"}
              </span>
            </div>
            {transaction.paymentCategory && (
              <div className="gx-row">
                <span className="gx-label gx-p12-b">
                  {t("payment_category")}
                </span>
                <span className="gx-rtp-value gx-p12-b">
                  {transaction.paymentCategory}
                </span>
              </div>
            )}
            <div className="gx-row">
              <span className="gx-label gx-p12-b">{t("payment_status")}</span>
              <span className="gx-rtp-value gx-p12-b">
                {transaction.paymentStatusDesc || "--"}
              </span>
            </div>

            {/* Recurring Details */}
            {transaction.soEnableFlag === "Y" && (
              <>
                <div className="gx-subsection-header">
                  <h2 className="gx-subsection-title gx-p14-b">
                    {t("recurring_details")}
                  </h2>
                </div>
                <div className="gx-row">
                  <span className="gx-label gx-p12-b">{t("start_date")}</span>
                  <span className="gx-rtp-value gx-p12-b">
                    {gxDateTimeFormat(transaction.soStartDate || "--")}
                  </span>
                </div>
                <div className="gx-row">
                  <span className="gx-label gx-p12-b">{t("end_date")}</span>
                  <span className="gx-rtp-value gx-p12-b">
                    {gxDateTimeFormat(transaction.soExpiryDate || "--")}
                  </span>
                </div>
                <div className="gx-row">
                  <span className="gx-label gx-p12-b">{t("frequency")}</span>
                  <span className="gx-rtp-value gx-p12-b">
                    {transaction.soExeFrequency || "--"}
                  </span>
                </div>
                {transaction.soExeDay && (
                  <div className="gx-row">
                    <span className="gx-label gx-p12-b">
                      {t("execution_days")}
                    </span>
                    <span className="gx-rtp-value gx-p12-b">
                      {transaction.soExeDay}
                    </span>
                  </div>
                )}
              </>
            )}

            {transaction.paymentInternalRemarks && (
              <div className="gx-internal-remarks">
                <span className="gx-p14-b">{t("internal_remarks")}</span>
                <p className="gx-p14">{transaction.paymentInternalRemarks}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* === Payment Timeline === */}
      {!schedule && (
        <div className="gx-card mt-4">
          <div
            className="gx-card-header"
            onClick={() => setIsPaymentProgressOpen(!isPaymentProgressOpen)}
          >
            <h2 className="gx-rtp_view-head gx-p18-b">
              {t("payment_timeline")}
            </h2>
            <SvgIcon
              icon={isPaymentProgressOpen ? chevronUpIcon : chevronDownIcon}
              size="large"
            />
          </div>

          {isPaymentProgressOpen && (
            <div className="gx-approval-progress">
              <ul className="gx-approval-timeline">
                {/* === Step 1: Payment Initiated === */}
                <li className="payment-progress__step">
                  <div className="payment-progress__icon-col">
                    <SvgIcon
                      icon={getStepSvgIcon("A")}
                      style={{ color: getStepIconColor("A") }}
                      className="payment-progress__icon"
                    />
                    <span className={getStepLineClass("A")} />
                  </div>

                  <div className="gx-step-connect">
                    <div
                      className="gx-approval-initiated"
                      onClick={() => toggleStep(0)}
                    >
                      <h4 className="gx-ape-completed">
                        <span className="gx-p14-md">Payment Initiated</span>
                        <span className="gx-crtdDt gx-p10">{initiatedAt}</span>
                      </h4>
                    </div>

                    <AnimatePresence>
                      {expandedSteps.has(0) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="gx-approval-card"
                        >
                          <div className="gx-approval-created">
                            <p className="gx-p10">Initiated by:</p>
                            <p className="gx-p12-m">{initiatedBy}</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </li>

                {/* === Step 2: Payment Status === */}
                <li className="payment-progress__step">
                  <div className="payment-progress__icon-col">
                    <SvgIcon
                      icon={getStepSvgIcon(overallStepStatus)}
                      style={{ color: getStepIconColor(overallStepStatus) }}
                      className="payment-progress__icon"
                    />
                    {/* No connector line after last step */}
                  </div>

                  <div className="gx-step-connect">
                    <div
                      className="gx-approval-initiated"
                      onClick={() => toggleStep(1)}
                    >
                      <h4 className="gx-ape-completed gx-p14-md">
                        <span className="gx-p12-md">{paymentStatusLabel}</span>
                        <span className="gx-crtdDt gx-p10">
                          {statusTimestamp}
                        </span>
                      </h4>
                    </div>

                    <AnimatePresence>
                      {expandedSteps.has(1) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="gx-approval-card"
                        >
                          <div className="gx-approval-created gx-p12-m payment-progress__approval-list">
                            {/* Approval count */}
                            <span className="gx-p12-m payment-progress__info-label">
                              {approvalsCompleted} of {approvalsRequired}{" "}
                              Approvals Completed
                            </span>

                            {/* Approvers list */}
                            {approverList.length > 0 && (
                              <ul className="gx-approvals-list">
                                {approverList.map((ap, idx) => (
                                  <li
                                    key={idx}
                                    className="gx-approval-list-item gx-p12-m"
                                  >
                                    <img
                                      src={getApproverImgIcon(ap.status)}
                                      alt={getApproverStatusLabel(ap.status)}
                                      className="gx-icon"
                                    />
                                    <span className="gx-approver-name gx-p12-m">
                                      {ap.approverName ||
                                        `Approver Level ${ap.level || idx + 1}`}
                                    </span>
                                    {ap.status !== "P" && ap.approvalDate && (
                                      <span className="gx-approval-date gx-p10-r">
                                        {ap.approvalDate}
                                      </span>
                                    )}
                                    <span
                                      className="payment-progress__status"
                                      style={{
                                        color: getStepIconColor(
                                          ap.status === "A"
                                            ? "A"
                                            : ap.status === "R"
                                              ? "R"
                                              : "P",
                                        ),
                                      }}
                                    />
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </li>
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
